import { Contact, EmailTemplate, HostlinksEvent } from '../../types';

const PLACEHOLDER_KEYS = [
  'LOCATION',
  'DATE',
  'INTRO',
  'LINK',
  'DISCOUNT',
  'EXTRA_INFO',
  'CONTACT_1',
  'CONTACT_2',
  'CONTACT_3',
] as const;

export interface EmailEntry {
  id: string;
  /** Template part this row renders (stable if rows are added/removed/skipped). */
  partIndex: number;
  subject: string;
  scheduledDate: Date | null;
  scheduledTime: string;
}

export interface TemplateBuild {
  templateId: string;
  segmentId: number | null;
  emails: EmailEntry[];
}

export interface LeftoverFields {
  LINK?: string;
  INTRO: string;
  DISCOUNT: string;
  EXTRA_INFO: string;
  CONTACT_1: string;
  CONTACT_2: string;
  CONTACT_3: string;
}

let _emailIdCounter = 0;
export const nextEmailId = () => `email-${++_emailIdCounter}`;

export function addDays(date: Date, days: number): Date {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
}

/** Combine DatePicker date + HH:mm in local time (same as Interspire queue). */
export function combineSchedule(date: Date, time: string): Date {
  const d = new Date(date);
  const [h, m] = String(time || '00:00').split(':').map((x) => parseInt(x, 10));
  d.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(m) ? m : 0, 0, 0);
  return d;
}

export function ymd(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function eventLabel(event: {
  id: string;
  location: string;
  dates_display: string;
  type: string;
  city?: string;
  state?: string;
}): string {
  const locationBit =
    event.location ||
    [event.city, event.state].filter(Boolean).join(', ') ||
    '';
  const parts = [locationBit, event.dates_display, event.type].filter(Boolean);
  return parts.join(' · ') || `Event ${event.id}`;
}

/** Every whitespace-separated token must appear in the searchable haystack. */
export function matchesHostlinksEvent(
  event: Pick<
    HostlinksEvent,
    | 'id'
    | 'location'
    | 'city'
    | 'state'
    | 'dates_display'
    | 'type'
    | 'type_abbr'
    | 'marketer'
    | 'instructor'
    | 'host_name'
  >,
  query: string
): boolean {
  const tokens = query
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean);
  if (tokens.length === 0) return true;
  const haystack = [
    event.location,
    event.city,
    event.state,
    event.dates_display,
    event.type,
    event.type_abbr,
    event.marketer,
    event.instructor,
    event.host_name,
    event.id,
  ]
    .map((v) => String(v ?? '').toLowerCase())
    .join(' ');
  return tokens.every((t) => haystack.includes(t));
}

const KIND_ORDER: Array<EmailTemplate['kind']> = ['standard', 'alumni', 'combo'];

export function templateKindLabel(kind: EmailTemplate['kind'] | string | undefined): string {
  if (kind === 'alumni') return 'Alumni';
  if (kind === 'combo') return 'Combo';
  if (kind === 'zoom') return 'Zoom';
  return 'Standard';
}

export function templatesGroupedByKind(templates: EmailTemplate[]): Array<{
  kind: EmailTemplate['kind'];
  label: string;
  templates: EmailTemplate[];
}> {
  const buckets = new Map<EmailTemplate['kind'], EmailTemplate[]>();
  for (const k of KIND_ORDER) buckets.set(k, []);
  for (const t of templates) {
    if (t.kind === 'zoom') continue;
    const kind: EmailTemplate['kind'] =
      t.kind === 'alumni' || t.kind === 'combo' ? t.kind : 'standard';
    buckets.get(kind)!.push(t);
  }
  return KIND_ORDER.filter((k) => (buckets.get(k) ?? []).length > 0).map((kind) => ({
    kind,
    label: templateKindLabel(kind),
    templates: buckets.get(kind)!,
  }));
}

/** Event type fields used to suggest templates (not location/dates — those are venue, not program). */
export function eventTypeHaystack(event: Pick<HostlinksEvent, 'type' | 'type_abbr'>): string {
  return [event.type, event.type_abbr]
    .map((v) => String(v ?? '').toLowerCase())
    .join(' ');
}

/**
 * Suggest a template when the event looks like that program (e.g. Management → GM).
 * Does not auto-select — UI only glows the chip.
 */
export function templateSuggestedForEvent(
  template: Pick<EmailTemplate, 'name'>,
  event: Pick<HostlinksEvent, 'type' | 'type_abbr'> | null
): boolean {
  if (!event) return false;
  const hay = eventTypeHaystack(event);
  const name = String(template.name ?? '').toLowerCase();
  if (!hay.trim() || !name.trim()) return false;

  const abbr = String(event.type_abbr ?? '').trim().toLowerCase();
  if (abbr.length >= 2) {
    const abbrRe = new RegExp(
      `(^|[^a-z0-9])${abbr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`,
      'i'
    );
    if (abbrRe.test(name)) return true;
  }

  const isManagement = /\bmanagement\b|\bgm\b|grant\s*management/.test(hay);
  const isWriting = /\bwriting\b|\bgw\b|grant\s*writing/.test(hay);

  if (isManagement && (/\bgm\b/.test(name) || /grant\s*management|\bmanagement\b/.test(name))) {
    return true;
  }
  if (isWriting && (/\bgw\b/.test(name) || (/writing/.test(name) && !/management/.test(name)))) {
    return true;
  }

  const type = String(event.type ?? '').trim().toLowerCase();
  if (type.length >= 4 && name.includes(type)) return true;

  return false;
}

export type ContentFields = {
  LOCATION: string;
  DATE: string;
  LINK: string;
  INTRO: string;
  DISCOUNT: string;
  EXTRA_INFO: string;
  CONTACT_1: string;
  CONTACT_2: string;
  CONTACT_3: string;
};

export function emptyContentFields(): ContentFields {
  return {
    LOCATION: '',
    DATE: '',
    LINK: '',
    INTRO: '',
    DISCOUNT: '',
    EXTRA_INFO: '',
    CONTACT_1: '',
    CONTACT_2: '',
    CONTACT_3: '',
  };
}

export function leftoversFromContent(c: ContentFields): LeftoverFields {
  return {
    LINK: c.LINK,
    INTRO: c.INTRO,
    DISCOUNT: c.DISCOUNT,
    EXTRA_INFO: c.EXTRA_INFO,
    CONTACT_1: c.CONTACT_1,
    CONTACT_2: c.CONTACT_2,
    CONTACT_3: c.CONTACT_3,
  };
}

/** First whitespace-separated token, lowercased. */
export function firstNameToken(name: string): string {
  return String(name ?? '')
    .trim()
    .split(/\s+/)[0]
    ?.toLowerCase() ?? '';
}

/** Unique first-name match against EmailCraft contacts; full name breaks ties. */
export function matchContactByFirstName<T extends { name: string }>(
  marketer: string,
  contacts: T[]
): T | null {
  const needle = firstNameToken(marketer);
  if (!needle) return null;
  const hits = contacts.filter((c) => firstNameToken(c.name) === needle);
  if (hits.length === 1) return hits[0];
  if (hits.length === 0) return null;
  const full = String(marketer).trim().toLowerCase();
  const exact = hits.filter((c) => c.name.trim().toLowerCase() === full);
  return exact.length === 1 ? exact[0] : null;
}

export function contactPlaceholderValue(contact: Pick<Contact, 'name' | 'details'>): string {
  return `${contact.name}\n${contact.details}`;
}

export function contact1FromMarketer(marketer: string, contacts: Contact[]): string {
  const hit = matchContactByFirstName(marketer, contacts);
  return hit ? contactPlaceholderValue(hit) : '';
}

/** Preview merge for unified build (event or manual content fields). */
export function htmlForPart(template: EmailTemplate, index: number): string {
  const bodies =
    template.htmlBodies && template.htmlBodies.length > 0
      ? template.htmlBodies
      : [template.htmlBody];
  const at = bodies[index];
  if (typeof at === 'string' && at.trim()) return at;
  for (let i = Math.min(index, bodies.length - 1); i >= 0; i--) {
    if (String(bodies[i] ?? '').trim()) return bodies[i];
  }
  return String(template.htmlBody ?? '').trim() ? template.htmlBody : '';
}

export function renderContentPreview(
  template: EmailTemplate,
  content: ContentFields,
  subjectLine: string,
  partIndex = 0
): { html: string; subject: string } {
  const placeholders = {
    LOCATION: content.LOCATION,
    DATE: content.DATE,
    LINK: content.LINK,
    INTRO: content.INTRO,
    DISCOUNT: content.DISCOUNT,
    EXTRA_INFO: content.EXTRA_INFO,
    CONTACT_1: content.CONTACT_1,
    CONTACT_2: content.CONTACT_2,
    CONTACT_3: content.CONTACT_3,
  };
  const html = renderEventTemplateHtml(htmlForPart(template, partIndex), placeholders);
  const subject = processSubject(subjectLine, placeholders.LOCATION, placeholders.DATE);
  return { html, subject };
}

export function processSubject(subject: string, location: string, date: string): string {
  return subject
    .replace(/\{\{LOCATION\}\}/g, location || '[LOCATION]')
    .replace(/\{\{DATE\}\}/g, date || '[DATE]');
}

/** Interspire newsletter name: "Email 1 - Loc - Date" or "Email 1 Alumni - Loc - Date". */
export function interspireCampaignName(
  partIndex: number,
  location: string,
  date: string,
  kind?: string
): string {
  const n = partIndex + 1;
  const prefix = kind === 'alumni' ? `Email ${n} Alumni` : `Email ${n}`;
  return `${prefix} - ${location || 'Location TBD'} - ${date || 'Date TBD'}`;
}

export function defaultFirstDate(): Date {
  return addDays(new Date(), 14);
}

export function emailsFromTemplate(
  template: EmailTemplate,
  location: string,
  date: string
): EmailEntry[] {
  const first = defaultFirstDate();
  const subjects = template.subjectLines ?? [];
  const bodies =
    template.htmlBodies && template.htmlBodies.length > 0
      ? template.htmlBodies
      : [template.htmlBody];
  const n = Math.max(subjects.length, bodies.length, 1);
  const rows: EmailEntry[] = [];
  for (let index = 0; index < Math.min(n, 10); index++) {
    const subject = subjects[index] ?? '';
    const html = bodies[index] ?? bodies[0] ?? '';
    if (!subject.trim() && !String(html).trim()) continue;
    rows.push({
      id: nextEmailId(),
      partIndex: index,
      subject: processSubject(subject, location, date),
      scheduledDate: addDays(first, rows.length * 14),
      scheduledTime: '06:00',
    });
  }
  if (rows.length === 0) {
    return [
      {
        id: nextEmailId(),
        partIndex: 0,
        subject: processSubject(subjects[0] ?? '', location, date),
        scheduledDate: first,
        scheduledTime: '06:00',
      },
    ];
  }
  return rows;
}

/** Merge Hostlinks event fields + manual leftovers (matches server mapHostlinksEventToPlaceholders). */
export function buildPlaceholderMap(
  event: Pick<HostlinksEvent, 'location' | 'dates_display' | 'reg_url' | 'web_url' | 'host_name'>,
  leftovers: LeftoverFields
): Record<(typeof PLACEHOLDER_KEYS)[number], string> {
  return {
    LOCATION: String(event.location ?? '').trim(),
    DATE: String(event.dates_display ?? '').trim(),
    LINK: leftovers.LINK !== undefined ? leftovers.LINK : String(event.web_url || '').trim(),
    INTRO:
      leftovers.INTRO !== undefined
        ? leftovers.INTRO
        : String(event.host_name ?? '').trim(),
    DISCOUNT: leftovers.DISCOUNT,
    EXTRA_INFO: leftovers.EXTRA_INFO,
    CONTACT_1: leftovers.CONTACT_1,
    CONTACT_2: leftovers.CONTACT_2,
    CONTACT_3: leftovers.CONTACT_3,
  };
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Match server applyLinkPlaceholder: body {{LINK}} becomes a real <a href>. */
function applyLinkPlaceholder(html: string, rawUrl: string): string {
  const url = rawUrl.trim();
  if (!url || /^\s*(javascript|data):/i.test(url)) {
    return html.replace(/\{\{LINK\}\}/g, '').replace(/\[LINK\]/g, '');
  }
  const safe = escapeHtml(url);
  const anchor = `<a href="${safe}">${safe}</a>`;
  let out = html;
  out = out.replace(/href=(["'])\{\{LINK\}\}\1/gi, `href=$1${safe}$1`);
  out = out.replace(/href=(["'])\[LINK\]\1/gi, `href=$1${safe}$1`);
  out = out.replace(/(<a\b[^>]*>)\s*\{\{LINK\}\}\s*(<\/a>)/gi, `$1${safe}$2`);
  out = out.replace(/(<a\b[^>]*>)\s*\[LINK\]\s*(<\/a>)/gi, `$1${safe}$2`);
  out = out.replace(/\{\{LINK\}\}/g, anchor);
  out = out.replace(/\[LINK\]/g, anchor);
  return out;
}

/** Final HTML for preview/test/queue (same rules as server renderTemplateHtml). */
export function renderEventTemplateHtml(htmlBody: string, placeholders: Record<string, string>): string {
  let htmlContent = applyLinkPlaceholder(htmlBody ?? '', placeholders.LINK ?? '');

  for (const key of PLACEHOLDER_KEYS) {
    if (key === 'LINK') continue;
    const value = placeholders[key] ?? '';
    const curlyRegex = new RegExp(`\\{\\{${key}\\}\\}`, 'g');
    const bracketRegex = new RegExp(`\\[${key}\\]`, 'g');

    if (value.trim() !== '') {
      let processedValue = value;
      if (key.startsWith('CONTACT_')) {
        processedValue = processedValue.replace(/\n/g, '<br>');
      }
      htmlContent = htmlContent.replace(curlyRegex, processedValue);
      htmlContent = htmlContent.replace(bracketRegex, processedValue);
    } else {
      htmlContent = htmlContent.replace(curlyRegex, '');
      htmlContent = htmlContent.replace(bracketRegex, '');
    }
  }

  htmlContent = htmlContent.replace(/\{\{[^}]*\}\}/g, '');
  htmlContent = htmlContent.replace(/\[[A-Z_]+\]/g, '');
  htmlContent = htmlContent.replace(/\n\s*\n\s*\n/g, '\n\n');
  htmlContent = htmlContent.replace(/(<\/[^>]+>)\s*\n\s*\n\s*(<[^>/][^>]*>)/g, '$1\n$2');

  return htmlContent.trim();
}

export function renderEventPreview(
  template: EmailTemplate,
  event: Pick<HostlinksEvent, 'location' | 'dates_display' | 'reg_url' | 'web_url' | 'host_name'>,
  leftovers: LeftoverFields,
  subjectLine: string
): { html: string; subject: string } {
  const placeholders = buildPlaceholderMap(event, leftovers);
  const html = renderEventTemplateHtml(htmlForPart(template, 0), placeholders);
  const subject = processSubject(subjectLine, placeholders.LOCATION, placeholders.DATE);
  return { html, subject };
}

/** Wrap fragment HTML so iframe srcDoc always gets a light-mode document. */
export function toEmailPreviewSrcDoc(html: string): string {
  const body = html?.trim() || '';
  if (/<html[\s>]/i.test(body)) {
    // Force light scheme even if the template omits it
    if (/color-scheme/i.test(body)) return body;
    return body.replace(
      /<head([^>]*)>/i,
      '<head$1><meta name="color-scheme" content="light only"><style>html,body{background:#fff;color:#000;}</style>'
    );
  }
  return `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="color-scheme" content="light only"><base target="_blank"><style>html,body{margin:0;padding:16px;background:#fff;color:#000;font-family:Arial,sans-serif;line-height:1.6;}</style></head><body>${body}</body></html>`;
}
